import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Alert } from '@/components/ui/Alert';
import { t } from '@/lib/i18n';
import { VisibilityPanel } from '@/components/roros/visibility/VisibilityPanel';
import type { EditorState } from '../roleContractEditorState';

interface Props {
  state: EditorState;
  setState: React.Dispatch<React.SetStateAction<EditorState>>;
  contractId: string | null;
}

export function ToolAccessPolicySection({ state, setState, contractId }: Props) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>{t('Tool Access Policy')}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Input
            label={t('Per-operator monthly budget cap (USD)')}
            type="number"
            min="0"
            step="0.01"
            value={state.toolBudgetCap}
            onChange={(e) => setState((s) => ({ ...s, toolBudgetCap: e.target.value }))}
            placeholder={t('Empty = no cap')}
            data-testid="role-contract-budget-cap-input"
          />
          <p className="text-xs text-muted-foreground">
            {t('Enforced at runtime by OutcomeDraftService — calls are rejected with ROROS_OPERATOR_BUDGET_EXHAUSTED when the cap is reached.')}
          </p>
          <div>
            <p className="text-sm font-medium mb-1">{t('Tool grants')}</p>
            <p className="text-xs text-muted-foreground mb-2">
              {/* Sven dogfood 2026-05-03: explain what this field is.
                  Pre-fix the only label was the cryptic format spec
                  with no description of what tools or what it does. */}
              {t('Authorize the AI Operator to call specific external tools (Slack, Gmail, HubSpot, etc.). One per line as "tool:level". Levels: read, write, admin. The OperatorExecutor refuses any call to a tool not on this list. A tool catalog picker is on the roadmap — for now, type the tool slug.')}
            </p>
            <Textarea
              rows={4}
              value={state.toolGrants.map((g) => `${g.tool}:${g.accessLevel}`).join('\n')}
              placeholder={t('slack:write\ngmail:read\nhubspot:read')}
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  toolGrants: e.target.value
                    .split('\n')
                    .map((l) => l.trim())
                    .filter(Boolean)
                    .map((line) => {
                      const [tool, accessLevel = 'read'] = line.split(':').map((p) => p.trim());
                      return { tool, accessLevel };
                    }),
                }))
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Sven dogfood 2026-05-03: clarify what Visibility is for. The
          panel was rendered with a one-line description but founders
          asked "what is this for?" Helper text added below the title. */}
      {contractId ? (
        <VisibilityPanel
          entityType="role_contract"
          entityId={contractId}
          title={t('Visibility')}
          description={t('Who can see this Role Contract and the artifacts it owns. Default is org-wide. Tighten this when the role carries sensitive context (CEO, board, finance) that should not be browsable by every employee.')}
        />
      ) : (
        <Alert variant="info">
          {t('Save the contract once before configuring visibility.')}
        </Alert>
      )}
    </div>
  );
}
